import React, { useState, useEffect, useRef } from 'react';
import { GameState } from '../types/game';
import { Card } from './Card';
import { findRecipe } from '../logic/recipes';
import { AlchemyTreeModal } from './AlchemyTreeModal';
import './GameBoard.css';

interface GameBoardProps {
  gameState: GameState;
  playerId: 1 | 2;
  isSpectator?: boolean;
  onCombine: (cardId1: string, cardId2: string) => void;
  onDiscard: (cardIds: string[]) => void;
  onEndTurn: () => void;
  onSurrender: () => void;
  onBackToMenu: () => void;
  combiningCards?: string[] | null;
}

export const GameBoard: React.FC<GameBoardProps> = ({
  gameState,
  playerId,
  isSpectator = false,
  onCombine,
  onDiscard,
  onEndTurn,
  onSurrender,
  onBackToMenu,
  combiningCards = null,
}) => {
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [mode, setMode] = useState<'combine' | 'discard'>('combine');
  const [animatingCard, setAnimatingCard] = useState<string | null>(null);
  const [isTreeModalOpen, setIsTreeModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(90);

  const onEndTurnRef = useRef(onEndTurn);

  const isMyTurn = gameState.currentTurn === playerId;
  const myState = playerId === 1 ? gameState.player1 : gameState.player2;
  const opponentState = playerId === 1 ? gameState.player2 : gameState.player1;
  const opponentId = playerId === 1 ? 2 : 1;

  useEffect(() => {
    onEndTurnRef.current = onEndTurn;
  }, [onEndTurn]);

  // Turn timer
  useEffect(() => {
    if (!isMyTurn) {
      setTimeLeft(90);
      return;
    }

    setTimeLeft(90);
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onEndTurnRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isMyTurn]);

  // Auto-return to lobby after game ends
  useEffect(() => {
    if (gameState.winner === null) return;
    const timeout = setTimeout(() => {
      onBackToMenu();
    }, 5000);
    return () => clearTimeout(timeout);
  }, [gameState.winner, onBackToMenu]);

  // R key for recipes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'r' || e.key === 'R' || e.key === 'к' || e.key === 'К') {
        setIsTreeModalOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCardClick = (cardId: string) => {
    if (!isMyTurn || animatingCard !== null) return;

    const clickedCard = [...myState.hand, ...gameState.table].find(c => c.id === cardId);
    if (!clickedCard) return;

    if (mode === 'combine') {
      if (selectedCards.includes(cardId)) {
        setSelectedCards(selectedCards.filter(id => id !== cardId));
      } else if (selectedCards.length === 0) {
        setSelectedCards([cardId]);
      } else if (selectedCards.length === 1) {
        const firstCard = [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0]);
        const secondCard = [...myState.hand, ...gameState.table].find(c => c.id === cardId);

        if (firstCard && secondCard) {
          const recipe = findRecipe(firstCard.element, secondCard.element);
          if (recipe) {
            onCombine(selectedCards[0], cardId);
            setSelectedCards([]);
            setAnimatingCard(cardId);
            setTimeout(() => setAnimatingCard(null), 300);
          } else {
            setSelectedCards([cardId]);
          }
        }
      }
    } else {
      if (selectedCards.includes(cardId)) {
        setSelectedCards(selectedCards.filter(id => id !== cardId));
      } else if (selectedCards.length < 4) {
        setSelectedCards([...selectedCards, cardId]);
      }
    }
  };

  const handleDiscard = () => {
    if (selectedCards.length === 0) {
      setMode('combine');
      return;
    }
    if (selectedCards.length > 0 && selectedCards.length <= 4) {
      onDiscard(selectedCards);
      setSelectedCards([]);
      setMode('combine');
    }
  };

  const handleEndTurn = () => {
    onEndTurn();
    setSelectedCards([]);
    setMode('combine');
  };

  const isCombinable = (cardId: string): boolean => {
    if (mode !== 'combine' || selectedCards.length !== 1 || selectedCards[0] === cardId) return false;

    const selectedCard = [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0]);
    const currentCard = [...myState.hand, ...gameState.table].find(c => c.id === cardId);
    if (!selectedCard || !currentCard) return false;

    if (findRecipe(selectedCard.element, currentCard.element) === null) return false;

    const selectedFromTable = gameState.table.some(c => c.id === selectedCards[0]);
    const currentFromTable = gameState.table.some(c => c.id === cardId);
    if (selectedFromTable && currentFromTable && myState.hand.length >= 6) return false;

    return true;
  };

  const isNotCombinable = (cardId: string): boolean => {
    if (mode !== 'combine' || selectedCards.length !== 1 || selectedCards[0] === cardId) return false;
    return !isCombinable(cardId);
  };

  return (
    <div className="game-board">
      {isSpectator && (
        <div className="spectator-banner">Spectating</div>
      )}

      {/* Opponent section with timer, surrender, recipes */}
      <div className="opponent-section">
        <div className="section-label">P{opponentId} ({opponentState.hand.length})</div>
        <div className="cards-container">
          {opponentState.hand.map((card) => (
            <Card
              key={card.id}
              card={card}
              size="small"
              disabled
              combining={combiningCards?.includes(card.id)}
            />
          ))}
        </div>
        <div className="opponent-controls">
          <span className={`turn-indicator ${isMyTurn ? 'my-turn' : 'opponent-turn'}`}>
            {isMyTurn ? 'Your turn' : 'Waiting'}
          </span>
          <span className={`timer-display ${isMyTurn ? (timeLeft <= 10 ? 'warning' : 'active') : ''}`}>
            {timeLeft}s
          </span>
          <button
            className="btn-compact btn-recipes"
            onClick={() => setIsTreeModalOpen(true)}
            title="R"
          >
            📖
          </button>
          <button
            className="btn-compact btn-surrender"
            onClick={onSurrender}
            disabled={gameState.winner !== null}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-section">
        <div className="section-label">Table</div>
        <div className="cards-container">
          <div className="spacer-100" />
          {gameState.table.map((card) => (
            <Card
              key={card.id}
              card={card}
              size="small"
              onClick={() => handleCardClick(card.id)}
              selected={selectedCards.includes(card.id)}
              combinable={isCombinable(card.id)}
              notCombinable={isNotCombinable(card.id)}
              disabled={!isMyTurn}
              animating={animatingCard === card.id}
              combining={combiningCards?.includes(card.id)}
            />
          ))}
          <div className="spacer-250" />
        </div>
      </div>

      {/* Player section with discard/end turn */}
      <div className="player-section">
        {mode === 'combine' && selectedCards.length === 1 && (
          <div className="recipe-hint">
            Select second card to combine
          </div>
        )}
        {mode === 'discard' && (
          <div className="recipe-hint">
            Select 1-4 cards to discard ({selectedCards.length} selected)
          </div>
        )}
        <div className="player-row">
          <div className="section-label">You ({myState.hand.length}/6)</div>
          <div className="cards-container">
            {myState.hand.map((card) => (
              <Card
                key={card.id}
                card={card}
                size="small"
                onClick={() => handleCardClick(card.id)}
                selected={selectedCards.includes(card.id)}
                combinable={isCombinable(card.id)}
                notCombinable={isNotCombinable(card.id)}
                disabled={!isMyTurn}
                animating={animatingCard === card.id}
                combining={combiningCards?.includes(card.id)}
              />
            ))}
          </div>
          <div className="player-controls">
            {mode === 'discard' ? (
              <>
                <button className="btn-action btn-discard active" onClick={handleDiscard} disabled={!isMyTurn}>
                  ✓ ({selectedCards.length})
                </button>
                <button className="btn-compact btn-cancel" onClick={() => { setMode('combine'); setSelectedCards([]); }}>
                  ✕
                </button>
              </>
            ) : (
              <button
                className="btn-action btn-discard"
                onClick={() => { setMode('discard'); setSelectedCards([]); }}
                disabled={!isMyTurn || myState.discardedThisTurn}
              >
                Discard
              </button>
            )}
            <button className="btn-action btn-end-turn" onClick={handleEndTurn} disabled={!isMyTurn}>
              End
            </button>
          </div>
        </div>
      </div>

      {/* Victory overlay */}
      {gameState.winner && (
        <div className="victory-overlay">
          <div className="victory-message">
            <h1>🜛</h1>
            <h2>{gameState.winner === playerId ? 'Victory!' : 'Defeat'}</h2>
            <p>Returning to lobby...</p>
            <button className="btn-compact btn-end-turn" onClick={onBackToMenu}>
              Back now
            </button>
          </div>
        </div>
      )}

      <AlchemyTreeModal
        isOpen={isTreeModalOpen}
        onClose={() => setIsTreeModalOpen(false)}
        selectedElement={
          selectedCards.length === 1
            ? [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0])?.element
            : null
        }
      />
    </div>
  );
};
